
import { validationMixin } from 'vuelidate';
import { defineComponent } from '@nuxtjs/composition-api';
import type { PropOptions } from 'vue';
import PublicPortfolioEditPersonalInfoModalFormCompaniesAutocomplete from './PublicPortfolioEditPersonalInfoModalFormCompaniesAutocomplete.vue';
import { formFields, inputNames, formFieldNames } from './constants';
import { generateForm, getValidation, generateAttrs } from './utils';
import type {
  FormConfigField,
  FormFieldsValidity,
  FormFieldsError,
  IndexableCurrentUser,
  IndexableConfiguration,
  Form,
} from './types';
import BravadoCheckboxV3 from '@/components/common/BravadoCheckboxV3/BravadoCheckboxV3.vue';
import BravadoText from '@/components/common/BravadoText/BravadoText.vue';
import BravadoTextInputV3 from '@/components/common/BravadoTextInputV3/BravadoTextInputV3.vue';
import PortfolioPublicityToggle from '@/components/partials/public-portfolio/PortfolioPublicityToggle/PortfolioPublicityToggle.vue';
import JobsMetroAreaInput from '@/components/partials/jobs/JobsMetroAreaInput.vue';
import type { Company } from '@/types/graphql';

export default defineComponent({
  name: 'PublicPortfolioEditPersonalInfoModalForm',
  components: {
    BravadoText,
    BravadoTextInputV3,
    BravadoCheckboxV3,
    PortfolioPublicityToggle,
    JobsMetroAreaInput,
    PublicPortfolioEditPersonalInfoModalFormCompaniesAutocomplete,
  },
  props: {
    user: {
      type: Object,
      required: true,
    } as PropOptions<IndexableCurrentUser>,
    configuration: {
      type: Object,
      required: true,
    } as PropOptions<IndexableConfiguration>,
  },
  mixins: [validationMixin],
  validations() {
    return {
      form: {
        ...getValidation(formFields),
      },
    };
  },
  data(): { form: Form; formFields: FormConfigField[] } {
    return {
      form: generateForm({ fields: formFields, user: this.user, configuration: this.configuration }),
      formFields,
    };
  },
  watch: {
    'form.hasOptedInToSms'(newHasOptedIn: boolean) {
      if (newHasOptedIn && Boolean(this.form[formFieldNames.PHONE])) {
        this.$notice.success('Subscribed!');
      }
    },
  },
  computed: {
    isFieldValid(): FormFieldsValidity {
      return formFields.reduce((acc: FormFieldsValidity, field: FormConfigField) => {
        if (!field.validation) return acc;

        acc[field.attributeName] = this.getIsFieldValid(field.attributeName);
        return acc;
      }, {});
    },
    formFieldsConfigured() {
      return this.formFields.map(field => {
        return {
          ...field,
          attrs: {
            ...field.attrs,
            ...this.getInputSpecificAttrs(field),
          },
          classList: {
            'jobs-edit-user-section-modal-form__field': true,
            'jobs-edit-user-section-modal-form__field--full': field.fullWidth,
            'jobs-edit-user-section-modal-form__field--w-public-toggle': field.hasPublicityToggle,
            'jobs-edit-user-section-modal-form__field--public-toggle':
              field.type === inputNames.PORTFOLIO_PUBLICITY_TOGGLE,
          },
        };
      });
    },

    fieldErrorMessage(): FormFieldsError {
      return Object.keys(this.form).reduce((acc: FormFieldsError, formFieldName) => {
        if (!this.$v.form[formFieldName]) return acc;

        const field = this.formFields.find(({ attributeName }) => attributeName === formFieldName);

        if (!field?.attrs?.label) return acc;

        return { ...acc, [formFieldName]: this.getRequiredFieldErrorMessage(formFieldName, field.attrs.label) };
      }, {});
    },
  },
  methods: {
    getIsFieldValid(fieldName: string): boolean {
      if (!this.$v.form?.$dirty) return true;

      const field = this.$v.form[fieldName];

      return !field?.$invalid;
    },
    getRequiredFieldErrorMessage(fieldName: string, fieldLabel: string): string {
      if (!this.$v.form?.$dirty) return '';

      const field = this.$v.form[fieldName];

      if (!field?.$invalid) return '';

      if (!field?.required) {
        return `Please provide ${fieldLabel}`;
      }

      return '';
    },
    getInputSpecificAttrs({ attributeName, type }: FormConfigField) {
      const hintAndIsValidAttrs = {
        isValid: this.isFieldValid[attributeName],
        hint: this.fieldErrorMessage[attributeName],
      };

      switch (type) {
        case inputNames.BRAVADO_TEXT_INPUT_V3:
          return hintAndIsValidAttrs;
        case inputNames.JOBS_METRO_AREA_INPUT:
          return {
            currentCity: this.user?.city,
            ...hintAndIsValidAttrs,
          };
        case inputNames.COMPANIES_AUTOCOMPLETE_V2:
          return {
            value: this.user?.company?.name ?? '',
            ...hintAndIsValidAttrs,
          };
        default:
          return {};
      }
    },
    onCompanyChange(company: Partial<Company>): void {
      this.form[formFieldNames.COMPANY_ID] = company.id;
    },
    onCompanyType(customCompany: { value: string; id: number }): void {
      if (customCompany.id) return;
      if (this.user?.company?.name === customCompany.value) return;

      this.form[formFieldNames.COMPANY_ID] = null;
    },
    save(): void {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;

      const { userAttrs, configurationAttrs } = generateAttrs({ fields: formFields, form: this.form });

      this.$emit('upsert', { userAttrs, configurationAttrs });
    },
    generateForm() {
      this.form = generateForm({ fields: formFields, user: this.user, configuration: this.configuration });
    },
  },
});
